/**
 * /admin/reels — light hub (no Mux asset list, no upload list, no secret shares).
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  BRAND_MUX_CREATOR_ID,
  BRAND_MUX_EXTERNAL_ID_FEED,
} from "@/lib/brandConfig";
import {
  Film,
  Flame,
  KeyRound,
  Link2,
  Loader2,
  RefreshCw,
} from "lucide-react";
import type { PrivateReelsConfig } from "./types";

const CARDS = [
  {
    to: "/admin/reels/videos",
    title: "Videos",
    body: "Subí, ordená y quitá reels de la playlist desde el admin.",
    icon: Film,
  },
  {
    to: "/admin/reels/acceso",
    title: "Acceso",
    body: "Password, modo de acceso y límites de sesión.",
    icon: KeyRound,
  },
  {
    to: "/admin/reels/links",
    title: "Links multi-uso",
    body: "Llave al feed live (sin elegir clips). TTL + usos.",
    icon: Link2,
  },
  {
    to: "/admin/reels/secret-share",
    title: "Secret share",
    body: "Subí reels, elegí videos y creá link con TTL en s o min.",
    icon: Flame,
  },
] as const;

export default function PrivateReelsHub() {
  const { getAuthHeaders } = useAdminAuth();
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState<PrivateReelsConfig | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      // light=1: skip Mux pagination (major TTFB win)
      const res = await fetch("/api/admin/private-reels?light=1", { headers });
      const data = (await res.json()) as {
        ok?: boolean;
        config?: PrivateReelsConfig;
        error?: string;
      };
      if (!res.ok || !data.ok || !data.config) {
        throw new Error(data.error || `HTTP ${res.status}`);
      }
      setConfig(data.config);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Error al cargar");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground py-8">
        <Loader2 className="h-4 w-4 animate-spin" /> Cargando resumen…
      </div>
    );
  }

  if (error || !config) {
    return (
      <div className="rounded-xl border border-red-500/30 bg-red-950/30 px-4 py-3 text-sm text-red-200">
        {error || "Sin config"}
        <Button type="button" size="sm" variant="ghost" className="ml-2" onClick={() => void load()}>
          Reintentar
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant={config.enabled ? "default" : "outline"}>
          {config.enabled ? "Habilitado" : "Deshabilitado"}
        </Badge>
        <Badge variant="outline" className="font-mono text-[10px]">
          {config.access_mode}
        </Badge>
        <Badge variant="outline" className="font-mono text-[10px]">
          source={config.playback_source}
        </Badge>
        <Badge variant="outline" className="font-mono text-[10px]">
          creator={BRAND_MUX_CREATOR_ID}
        </Badge>
        <Badge variant="outline" className="font-mono text-[10px]">
          ext={BRAND_MUX_EXTERNAL_ID_FEED}
        </Badge>
        <Button type="button" size="sm" variant="ghost" onClick={() => void load()}>
          <RefreshCw className="h-3.5 w-3.5 mr-1" /> Actualizar
        </Button>
      </div>

      <p className="text-xs text-muted-foreground max-w-2xl">
        Reels puede estar vacío hasta que subas videos en Videos. Cada
        capacidad vive en su subpágina para no cargar Mux Uploader, listas de
        uploads ni secret shares de golpe.
      </p>

      <div className="grid gap-3 sm:grid-cols-2">
        {CARDS.map((c) => (
          <Link
            key={c.to}
            to={c.to}
            className="group rounded-2xl border border-white/[0.08] bg-zinc-900/40 p-4 transition hover:border-violet-500/35 hover:bg-zinc-900/70"
          >
            <div className="flex items-start gap-3">
              <div className="rounded-xl border border-white/10 bg-black/30 p-2 text-violet-300 group-hover:border-violet-500/30">
                <c.icon className="h-4 w-4" />
              </div>
              <div>
                <h2 className="text-sm font-semibold text-white/95">{c.title}</h2>
                <p className="mt-1 text-[11px] text-muted-foreground leading-relaxed">
                  {c.body}
                </p>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
